import { useState } from "react";
import { useParams } from "react-router";
import { useFetch } from "../../hooks/useFetch";

type UserView = {
	id: string;
	name: string;
	email: string;
	role: string | null;
	banned: boolean | null;
	createdAt: string;
};

type SessionView = {
	id: string;
	createdAt: string;
	ipAddress: string | null;
	userAgent: string | null;
};

export function AdminTenantDetailPage() {
	const { slug } = useParams<{ slug: string }>();
	const {
		data: users,
		loading,
		error,
	} = useFetch<UserView[]>(`/api/admin/tenants/${slug}/users`);
	const [expandedUser, setExpandedUser] = useState<string | null>(null);
	const [sessions, setSessions] = useState<SessionView[]>([]);
	const [sessionsLoading, setSessionsLoading] = useState(false);
	const [resetLoading, setResetLoading] = useState<string | null>(null);
	const [resetMessage, setResetMessage] = useState<string | null>(null);
	const [impersonateLoading, setImpersonateLoading] = useState<string | null>(
		null,
	);

	async function handleViewSessions(userId: string) {
		if (expandedUser === userId) {
			setExpandedUser(null);
			return;
		}
		setSessionsLoading(true);
		setExpandedUser(userId);
		try {
			const res = await fetch(
				`/api/admin/tenants/${slug}/users/${userId}/sessions`,
				{ credentials: "include" },
			);
			if (res.ok) {
				setSessions(await res.json());
			}
		} finally {
			setSessionsLoading(false);
		}
	}

	async function handleResetPassword(userId: string) {
		if (!confirm("Trigger a password reset for this user?")) return;
		setResetLoading(userId);
		setResetMessage(null);
		try {
			const res = await fetch(
				`/api/admin/tenants/${slug}/users/${userId}/reset-password`,
				{ method: "POST", credentials: "include" },
			);
			if (res.ok) {
				setResetMessage("Password reset initiated.");
			} else {
				setResetMessage("Failed to trigger password reset.");
			}
		} catch {
			setResetMessage("Network error.");
		} finally {
			setResetLoading(null);
		}
	}

	async function handleImpersonate(userId: string, userName: string) {
		if (!confirm(`Impersonate as ${userName}?`)) return;
		setImpersonateLoading(userId);
		try {
			const res = await fetch(
				`/api/admin/tenants/${slug}/users/${userId}/impersonate`,
				{ method: "POST", credentials: "include" },
			);
			if (res.ok) {
				window.location.href = `/${slug}`;
			} else {
				setResetMessage("Failed to start impersonation.");
			}
		} catch {
			setResetMessage("Network error.");
		} finally {
			setImpersonateLoading(null);
		}
	}

	return (
		<div>
			<h1 className="text-2xl font-bold text-gray-900 mb-1">Tenant: {slug}</h1>
			<p className="text-sm text-gray-500 mb-4">Users and support operations</p>

			{error && <p className="text-red-600 text-sm mb-3">{error}</p>}
			{resetMessage && (
				<p className="text-blue-600 text-sm mb-3">{resetMessage}</p>
			)}

			{loading && !users && <p className="text-gray-500 text-sm">Loading…</p>}

			{users && (
				<div className="overflow-x-auto">
					<table className="w-full text-sm text-left">
						<thead className="bg-gray-100 text-gray-700">
							<tr>
								<th className="px-4 py-2">Name</th>
								<th className="px-4 py-2">Email</th>
								<th className="px-4 py-2">Role</th>
								<th className="px-4 py-2">Banned</th>
								<th className="px-4 py-2">Actions</th>
							</tr>
						</thead>
						<tbody>
							{users.map((u) => (
								<>
									<tr key={u.id} className="border-b border-gray-200">
										<td className="px-4 py-2 font-medium">{u.name}</td>
										<td className="px-4 py-2 text-gray-600">{u.email}</td>
										<td className="px-4 py-2 text-gray-600">{u.role ?? "—"}</td>
										<td className="px-4 py-2">
											{u.banned ? (
												<span className="text-xs text-red-600">Yes</span>
											) : (
												<span className="text-xs text-gray-400">No</span>
											)}
										</td>
										<td className="px-4 py-2 flex gap-2">
											<button
												type="button"
												onClick={() => handleViewSessions(u.id)}
												className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50"
											>
												{expandedUser === u.id
													? "Hide Sessions"
													: "View Sessions"}
											</button>
											<button
												type="button"
												disabled={resetLoading === u.id}
												onClick={() => handleResetPassword(u.id)}
												className="text-xs px-2 py-1 rounded border border-orange-300 text-orange-600 hover:bg-orange-50 disabled:opacity-50"
											>
												{resetLoading === u.id
													? "Resetting…"
													: "Reset Password"}
											</button>
											<button
												type="button"
												disabled={impersonateLoading === u.id}
												onClick={() => handleImpersonate(u.id, u.name)}
												className="text-xs px-2 py-1 rounded border border-purple-300 text-purple-600 hover:bg-purple-50 disabled:opacity-50"
											>
												{impersonateLoading === u.id
													? "Impersonating…"
													: "Impersonate"}
											</button>
										</td>
									</tr>
									{expandedUser === u.id && (
										<tr key={`${u.id}-sessions`} className="bg-gray-50">
											<td colSpan={5} className="px-4 py-3">
												{sessionsLoading ? (
													<p className="text-sm text-gray-500">
														Loading sessions…
													</p>
												) : sessions.length === 0 ? (
													<p className="text-sm text-gray-500">
														No sessions found.
													</p>
												) : (
													<table className="w-full text-xs">
														<thead>
															<tr className="text-gray-500">
																<th className="text-left py-1">Created</th>
																<th className="text-left py-1">IP Address</th>
																<th className="text-left py-1">User Agent</th>
															</tr>
														</thead>
														<tbody>
															{sessions.map((s) => (
																<tr
																	key={s.id}
																	className="border-t border-gray-200"
																>
																	<td className="py-1">
																		{new Date(s.createdAt).toLocaleString()}
																	</td>
																	<td className="py-1">{s.ipAddress ?? "—"}</td>
																	<td className="py-1 max-w-xs truncate">
																		{s.userAgent ?? "—"}
																	</td>
																</tr>
															))}
														</tbody>
													</table>
												)}
											</td>
										</tr>
									)}
								</>
							))}
							{users.length === 0 && (
								<tr>
									<td
										colSpan={5}
										className="px-4 py-8 text-center text-gray-500"
									>
										No users found.
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
}
